import React, { useState } from 'react';

declare function gtag(...args: unknown[]): void;

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d74aah75hic0eqh3131g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        if (typeof gtag === 'function') {
          gtag('event', 'conversion', {
            send_to: 'AW-17076629084/v2T0CLqaiZgcENzc4s4_',
          });
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm sm:text-base font-bold mb-4">
            🚨 FREE NO-OBLIGATION ESTIMATE
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0f1f3d] mb-4">
            Stop the Damage — Call Now for Same-Day Service
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mb-2">
            Call now to stop the damage and get a <strong>fast, accurate answer</strong> today — or fill out the form and we'll respond same day.{' '}
            <a href="tel:7252675482" className="text-red-600 font-bold hover:text-red-700">
              (725) 267-5482
            </a>.
          </p>
          <p className="text-sm sm:text-base text-[#0f1f3d] font-semibold max-w-2xl mx-auto">
            Speak directly to a real plumber — no call centers, no middlemen.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form - Full width on mobile, 2/3 on desktop */}
          <div className="lg:col-span-2">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10"
              data-readdy-form
              id="leak-detectives-contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm sm:text-base font-bold text-[#0f1f3d] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 text-base sm:text-lg border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm sm:text-base font-bold text-[#0f1f3d] mb-2">
                    Email Address <span className="text-gray-400 font-normal text-xs">(optional)</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-4 text-base sm:text-lg border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm sm:text-base font-bold text-[#0f1f3d] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 text-base sm:text-lg border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              <div className="mb-6 sm:mb-8">
                <label htmlFor="message" className="block text-sm sm:text-base font-bold text-[#0f1f3d] mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-4 text-base sm:text-lg border-2 border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all resize-none"
                  placeholder="Tell us about your plumbing issue..."
                ></textarea>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                  {formData.message.length}/500 characters
                </p>
              </div>

              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                <p className="text-red-700 text-sm font-semibold">
                  <i className="ri-alarm-warning-fill mr-1"></i>
                  Most leaks are found quickly — call now for immediate service.
                </p>
              </div>

              <a
                href="tel:7252675482"
                className="flex items-center justify-center gap-3 w-full bg-red-600 text-white font-black text-lg sm:text-xl py-5 rounded-lg hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer mb-3"
              >
                <i className="ri-phone-fill text-2xl"></i>
                Call Now — (725) 267-5482
              </a>

              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">or submit for a callback</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0f1f3d] text-white font-bold text-base sm:text-lg py-4 sm:py-5 rounded-lg hover:bg-[#162447] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Get Free Estimate
                  </span>
                )}
              </button>
              <p className="text-center text-sm text-gray-500 mt-2">Same-day response • No obligation • Immediate service available</p>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                  <p className="text-green-700 font-semibold text-center text-sm sm:text-base">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    Message sent successfully! We'll get back to you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                  <p className="text-red-700 font-semibold text-center text-sm sm:text-base">
                    <i className="ri-error-warning-fill mr-2"></i>
                    Something went wrong. Please try again or call us directly.
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Contact Info - Hidden on mobile, visible on desktop */}
          <div className="hidden lg:block">
            <div className="bg-gradient-to-br from-[#0a1628] to-[#162447] rounded-2xl shadow-xl p-8 text-white h-full">
              <h3 className="text-2xl font-black mb-6">Immediate Service Available</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-lg flex-shrink-0">
                    <i className="ri-phone-fill text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Call Now — We Answer 24/7</h4>
                    <a
                      href="tel:7252675482"
                      className="text-cyan-400 hover:text-cyan-300 text-lg font-semibold"
                    >
                      (725) 267-5482
                    </a>
                    <p className="text-gray-300 text-sm mt-1">Speak directly to a specialist</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-fill text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Service Area</h4>
                    <p className="text-gray-300">
                      Las Vegas, NV
                      <br />
                      Clark County &amp; Surrounding Areas
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0">
                    <i className="ri-time-fill text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Hours</h4>
                    <p className="text-gray-300">
                      24/7 Emergency Service
                      <br />
                      Same-Day Repairs — 365 Days a Year
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-600">
                <h4 className="font-bold text-lg mb-4">Why Act Now?</h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-red-400 text-xl"></i>
                    <span className="text-sm">Free No-Obligation Estimate</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-cyan-400 text-xl"></i>
                    <span className="text-sm">Same-Day Repairs Available</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-cyan-400 text-xl"></i>
                    <span className="text-sm">Immediate Service — Call &amp; We Come</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-cyan-400 text-xl"></i>
                    <span className="text-sm">Licensed, Insured &amp; 5-Star Rated</span>
                  </li>
                </ul>
              </div>

              <div className="mt-6">
                <a
                  href="tel:7252675482"
                  className="block w-full bg-red-600 text-white font-black text-base sm:text-lg py-4 rounded-xl hover:bg-red-500 transition-colors cursor-pointer text-center whitespace-nowrap"
                >
                  <i className="ri-phone-fill mr-2"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
